import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Card, Space, Table, Typography, message } from "antd";
import type { ColumnsType } from "antd/es/table";
import { DownloadOutlined, ReloadOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { useAuth } from "../../contexts/auth.context";
import { hasAdminAccess } from "../../utils/roleUtils";
import {
  downloadSavedTurnoverDetailsExcelReport,
  generateTurnoverDetailsExcelReport,
  listTurnoverDetailsExcelReports,
} from "../../api/api";

const { Title, Text } = Typography;

type ReportRow = {
  key: string;
  date: string;
  fileName: string;
  sizeBytes: number;
  createdAtMs: number;
};

function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return "0 B";
  const units = ["B", "KB", "MB", "GB", "TB"];
  const i = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  const value = bytes / Math.pow(1024, i);
  const digits = value >= 100 || i === 0 ? 0 : value >= 10 ? 1 : 2;
  return `${value.toFixed(digits)} ${units[i]}`;
}

export default function TurnoverExcelReportsPage() {
  const { authState } = useAuth();
  const isAdmin = hasAdminAccess(authState.user?.role);

  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [rows, setRows] = useState<ReportRow[]>([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await listTurnoverDetailsExcelReports();
      const mapped: ReportRow[] = (data.reports || []).map((r) => ({
        key: r.date,
        date: r.date,
        fileName: r.fileName,
        sizeBytes: r.sizeBytes,
        createdAtMs: r.createdAtMs,
      }));
      setRows(mapped);
    } catch (e) {
      console.error(e);
      message.error("Не удалось загрузить архив отчетов");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isAdmin) {
      load();
    }
  }, [isAdmin, load]);

  const columns: ColumnsType<ReportRow> = useMemo(
    () => [
      {
        title: "Дата",
        dataIndex: "date",
        key: "date",
        width: 140,
        sorter: (a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0),
        defaultSortOrder: "descend",
      },
      {
        title: "Размер",
        dataIndex: "sizeBytes",
        key: "sizeBytes",
        width: 140,
        render: (v: number) => <Text>{formatBytes(v)}</Text>,
      },
      {
        title: "Создан",
        dataIndex: "createdAtMs",
        key: "createdAtMs",
        width: 200,
        render: (v: number) => <Text>{dayjs(v).format("DD.MM.YYYY HH:mm")}</Text>,
      },
      {
        title: "Действия",
        key: "actions",
        width: 180,
        render: (_, row) => (
          <Space>
            <Button
              icon={<DownloadOutlined />}
              onClick={() => downloadSavedTurnoverDetailsExcelReport(row.date)}
            >
              Скачать
            </Button>
          </Space>
        ),
      },
    ],
    []
  );

  const onGenerateToday = async () => {
    setGenerating(true);
    try {
      const result = await generateTurnoverDetailsExcelReport();
      if (result.status === "skipped") {
        message.info(`Отчет за ${result.date} уже существует`);
      } else if (result.status === "regenerated") {
        message.success(`Отчет за ${result.date} пересоздан`);
      } else {
        message.success(`Отчет за ${result.date} создан`);
      }
      await load();
    } catch (e) {
      console.error(e);
      message.error("Не удалось сгенерировать отчет");
    } finally {
      setGenerating(false);
    }
  };

  if (!isAdmin) {
    return (
      <Card style={{ maxWidth: 900, margin: "24px auto" }}>
        <Title level={3} style={{ marginBottom: 8 }}>
          Архив Excel отчетов по обороту
        </Title>
        <Text type="danger">Доступ только для администратора.</Text>
      </Card>
    );
  }

  return (
    <Card style={{ maxWidth: 1100, margin: "24px auto" }}>
      <Space direction="vertical" size="middle" style={{ width: "100%" }}>
        <Space style={{ width: "100%", justifyContent: "space-between" }}>
          <Title level={3} style={{ margin: 0 }}>
            Архив Excel отчетов по обороту
          </Title>
          <Space>
            <Button icon={<ReloadOutlined />} onClick={load} loading={loading}>
              Обновить
            </Button>
            <Button type="primary" onClick={onGenerateToday} loading={generating}>
              Сгенерировать за сегодня
            </Button>
          </Space>
        </Space>

        <Table
          columns={columns}
          dataSource={rows}
          loading={loading}
          pagination={{ pageSize: 20 }}
          size="middle"
        />
      </Space>
    </Card>
  );
}

